<script>
   /**************************************************************/
   /* AppContainer for training                                  */
   /* -------------------------                                  */
   /* app container for training of SIMCA model                  */
   /**************************************************************/


   import { downloadResults, downloadModel, simca_predict } from '../utils';

   import DataLoader from '../../../shared/components/PCADataLoader.svelte';

   import PCVCalculator from './PCVCalculator.svelte';
   import ModelCalculator from './ModelCalculator.svelte';
   import AppPlots from './AppPlots.svelte';

   import ResultTable from '../shared/ResultTable.svelte';
   import ModelTable from '../shared/ModelTable.svelte';
   import SIMCASettings from '../shared/SIMCASettings.svelte';
   import ContributionsModal from '../modals/Contributions.svelte';

   import Outliers from '../../../shared/widgets/Outliers.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   export let WASM; // WASM object from parent app


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // main properties of the training app
   // all set to undefined to avoid triggering
   // reset conditions on first render

   let model = undefined;     // global PCA model
   let simca = undefined;     // SIMCA parameters
   let calset = undefined;    // calibration set
   let pvset = undefined;     // computed PV-set
   let className = undefined; // name of target class


   // parameters to check if model or simca object have been updated
   let modelHash = 0;
   let simcaHash = 0;

   // settings for outliers
   let outliers = [];         // vector with dataset row numbers (from 0) marked as outliers

   // settings for results
   let calres = undefined;    // calibration PCA results
   let pvres = undefined;     // PV-set PCA results
   let simcares = undefined;  // combined results for SIMCA classification
   let ncomp = 1;             // number of components selected by user

   // extra variables to combine PCA results and datasets for scores plot
   let pcares = undefined;
   let datasets = undefined;

   // flag and object id container for showing contributions plot
   let isContributionsModalShown = false;
   let outlierId = -1;


   /**
    * Show contributions plot for the selected object
    * @param e - event dispatched by AppPlots component with id of the selected object
    */
   function showContributionsModal(e) {
      const pos = parseInt(e.detail.id);
      if (Number.isNaN(pos) || pos < 0 || pos >= calres.objindices.length ) return;

      // find sample ID corresponding to the position
      const id = calres.objindices[pos];
      if (id >= 0 && !outliers.includes(id) && calres.objindices.includes(id)) {
         outlierId = id;
         isContributionsModalShown = true;
      }
   }


   /**
    * Close contributions plot
    * @param e - event dispatched by ContributionsPlot component
    */
   function closeContributionsModal(e) {
      outlierId = -1;
      isContributionsModalShown = false;
   }


   /**
    * Set the selected point as outlier and reset the model and results
    * @param e - event dispatched by AppPlots component with id of the selected point
    */
   function setOutlier(e) {
      // get position (index) of the sample to be removed
      const pos = parseInt(e.detail.id);
      if (Number.isNaN(pos) || pos < 0 || pos >= calres.objindices.length ) return;

      // find sample ID corresponding to the position
      const id = calres.objindices[pos];

      // filter the outlier out
      if (id >= 0 && !outliers.includes(id)) {
         outliers = [...outliers, id];
         model = null;
      }
   }


   /**
    * Reset outliers and samples arrays
    */
   function resetOutliers() {
      outliers = [];
      model = null;
   }


   // if training set is set null - reset model and class name
   $: if (calset === null) {
      model = null;
      className = null;
      ncomp = 1;
      outliers = [];
   } else if (calset && !className) {
      className = calset.classNames[0];
   }

   // if model object is set to null - reset dependent objects
   $: if (model === null) {
      simca = null;
      calres = null;
      pvset = null;
      modelHash = 0;
      simcaHash = 0;
   }

   // if pvset is set null - reset the corresponding result object
   $: if (pvset === null) {
      simcares = null;
      pvres = null;
      simcaHash = 0;
   }

   // check hashes and initiate updates if outcomes changed
   $: if (model && model.hash !== modelHash) {
      modelHash = model.hash;
   }

   $: if (simca && simca.hash !== simcaHash && pvres && calres) {
      simcaHash = simca.hash;
      simcares = {
         cal: simca_predict(calres, simca, outliers),
         pv: simca_predict(pvres, simca, outliers)
      };

      // extra containers for PCA results and datasets
      pcares = {cal: calres, pv: pvres};
      datasets = {cal: calset, pv: pvset};
   }
</script>

{#if isContributionsModalShown}
<ContributionsModal data={calset} {model} {ncomp} {outliers} selected={outlierId} on:close={closeContributionsModal} />
{/if}

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={calset} bind:res={calres} {WASM} {model} {outliers}
         classMandatory={true} classUnique={true}/>
      </div>
      {#if outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers dataset={calset} on:resetoutliers={resetOutliers} bind:outliers={outliers} />
      </div>
      {/if}
      {#if calset }
      <div class="model-calculator">
         <ModelCalculator {WASM} {calset} {outliers} bind:model={model} bind:calres={calres} />
      </div>
      {/if}
      {#if model }
      <div class="pcv-calculator">
         <PCVCalculator {WASM} {calset} {outliers} {model} {calres} bind:pvset={pvset} bind:pvres={pvres} />
      </div>
      {/if}
      <div class="simca-settings">
         {#if pvset}
         <SIMCASettings {model} {className} bind:simca={simca} bind:ncomp={ncomp} />
         {/if}
         {#if simcares}
         <Container label='Download'>
            <div class="buttons">
            <Button on:click={() => downloadResults(simcares.cal, calset, "train", outliers, ncomp)}>Res (cal)</Button>
            <Button on:click={() => downloadResults(simcares.pv, pvset, "PV", outliers, ncomp)}>Res (pv)</Button>
            <Button on:click={() => downloadModel(model, simca, ncomp)}>Model</Button>
            </div>
         </Container>
         {/if}
      </div>
      {#if simcares}
      <div class="model-stats">
         <ModelTable {simca} {ncomp} />
      </div>
      <div class="result-stats">
         <ResultTable {simcares} {ncomp} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !pvset || !pvres || !simca}
         <div class="help-text">
         {#if !calset}
            <p>
               In this tab you can train a DD-SIMCA model and optimize its parameters using Procrustes cross-validation. After creating the model you can save it to a file and use it in other tabs. For example, validate and tune it using test set with target class members (tab <strong>Predict: Target</strong>) or apply it to a dataset with non-target class members (tab <strong>Predict: New</strong>).
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load your training set from a CSV file. The file should contain data as numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows correspond to observations or objects, columns — to variables.
            </p>
            <p>
               First column must contain class names for each row. All rows in the training set should belong to the target class, so it is expected that the class names are the same (you will get error message if not).
            </p>
            <p>
               First row can contain variable names (<strong>header</strong>) and first column can contain labels for the objects/observations (<strong>row labels</strong>) — just set the corresponding options after selecting the file. In this case it is assumed that column with the class names is located next to the column with row labels. Here is an example:
            </p>
            <div>
               <table>
               <tr>
                  <td>Name</td>
                  <td>Class</td>
                  <td>x1</td>
                  <td>x2</td>
                  <td>x3</td>
               </tr>
               <tr>
                  <td>O1</td>
                  <td>A</td>
                  <td>3.8</td>
                  <td>1.3</td>
                  <td>13.6</td>
               </tr>
               <tr>
                  <td>O2</td>
                  <td>A</td>
                  <td>11.1</td>
                  <td>12.3</td>
                  <td>3.3</td>
               </tr>
               <tr>
                  <td>O3</td>
                  <td>A</td>
                  <td>1.2</td>
                  <td>2.3</td>
                  <td>4.2</td>
               </tr>
               </table>
            </div>
            <p></p>
         {:else}
            {#if !model}
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to create a global PCA model for the calibration set, which will be the basis for the SIMCA model. Use more components/latent variables (PCs) than you expect to be optimal. You will have possibility to set
               optimal number later.
            </p>
            {/if}

            {#if model && !pvset}
            <p><strong>Step 3</strong></p>
            <p>
               Now set necessary parameters for Procrustes cross-validation and click button to compute the PV-set, which will help you to optimize SIMCA model parameters. The parameters are the same as for conventional cross-validation: type of splits and number of segments. It is recommended to use fewer number of segments (e.g. 3, 4 or 5).
            </p>
            <p>
               After this step SIMCA model will be created automatically and you will be able to tune its parameters.
            </p>
            {/if}
         {/if}
         </div>
      {:else}
         {#key pvset }
         <AppPlots on:setoutlier={setOutlier} on:plotoutlier={showContributionsModal}
            {model} {pcares} {simcares} {ncomp} {datasets} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      background: #e8e8e8;
      border-radius: 0.25rem;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table tr:first-child td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .model-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.5rem;
      font-size: 0.85em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }

   .buttons {
      position: relative;
      box-sizing: border-box;
      display: inline-block;
      flex: 1 1 auto;
      margin: 0;
      padding: 2px 0;
      width: auto;
      border-radius: 2px;
      cursor: default;
      white-space: nowrap;
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>